import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Select, 
  Button, 
  Row, 
  Col, 
  message, 
  Spin,
  Modal,
  Space,
  Divider,
  Typography 
} from 'antd';
import { 
  SaveOutlined, 
  GoogleOutlined, 
  FacebookOutlined, 
  BarChartOutlined,
  ExclamationCircleOutlined,
  ClearOutlined,
  DatabaseOutlined 
} from '@ant-design/icons';
import api from '../../services/api';
import { auditAPI } from '../../services/api';

const { TextArea } = Input;
const { Option } = Select;
const { Title } = Typography;

// Date range options
const GOOGLE_ADS_DATE_RANGES = [
  { value: 'TODAY', label: 'Bugün' },
  { value: 'YESTERDAY', label: 'Dün' },
  { value: 'LAST_7_DAYS', label: 'Son 7 Gün' },
  { value: 'LAST_14_DAYS', label: 'Son 14 Gün' },
  { value: 'LAST_30_DAYS', label: 'Son 30 Gün' },
  { value: 'LAST_BUSINESS_WEEK', label: 'Geçen İş Haftası' },
  { value: 'THIS_MONTH', label: 'Bu Ay' },
  { value: 'LAST_MONTH', label: 'Geçen Ay' },
  { value: 'THIS_WEEK_SUN_TODAY', label: 'Bu Hafta (Pazar-Bugün)' },
  { value: 'THIS_WEEK_MON_TODAY', label: 'Bu Hafta (Pazartesi-Bugün)' },
  { value: 'LAST_WEEK_SUN_SAT', label: 'Geçen Hafta (Pazar-Cumartesi)' },
  { value: 'LAST_WEEK_MON_SUN', label: 'Geçen Hafta (Pazartesi-Pazar)' }
];

const FACEBOOK_DATE_RANGES = [
  { value: 'today', label: 'Bugün' },
  { value: 'yesterday', label: 'Dün' },
  { value: 'last_3d', label: 'Son 3 Gün' },
  { value: 'last_7d', label: 'Son 7 Gün' },
  { value: 'last_14d', label: 'Son 14 Gün' },
  { value: 'last_28d', label: 'Son 28 Gün' },
  { value: 'last_30d', label: 'Son 30 Gün' },
  { value: 'last_90d', label: 'Son 90 Gün' },
  { value: 'this_week_mon_today', label: 'Bu Hafta (Pazartesi-Bugün)' },
  { value: 'this_week_sun_today', label: 'Bu Hafta (Pazar-Bugün)' },
  { value: 'last_week_mon_sun', label: 'Geçen Hafta (Pazartesi-Pazar)' },
  { value: 'last_week_sun_sat', label: 'Geçen Hafta (Pazar-Cumartesi)' },
  { value: 'this_month', label: 'Bu Ay' },
  { value: 'last_month', label: 'Geçen Ay' },
  { value: 'this_quarter', label: 'Bu Çeyrek' },
  { value: 'last_quarter', label: 'Geçen Çeyrek' },
  { value: 'this_year', label: 'Bu Yıl' },
  { value: 'last_year', label: 'Geçen Yıl' }
];

const GOOGLE_ANALYTICS_DATE_RANGES = [
  { value: 'today', label: 'Bugün' },
  { value: 'yesterday', label: 'Dün' },
  { value: 'last_7_days', label: 'Son 7 Gün' },
  { value: 'last_14_days', label: 'Son 14 Gün' },
  { value: 'last_30_days', label: 'Son 30 Gün' },
  { value: 'last_90_days', label: 'Son 90 Gün' },
  { value: 'this_week', label: 'Bu Hafta' },
  { value: 'last_week', label: 'Geçen Hafta' },
  { value: 'this_month', label: 'Bu Ay' },
  { value: 'last_month', label: 'Geçen Ay' }
];

const CUSTOM_DATA_DATE_RANGES = [
  { value: 'today', label: 'Bugün' },
  { value: 'yesterday', label: 'Dün' },
  { value: 'this_week', label: 'Bu Hafta' },
  { value: 'last_week', label: 'Geçen Hafta' },
  { value: 'this_month', label: 'Bu Ay' },
  { value: 'last_month', label: 'Geçen Ay' }
];

const AuditSettings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [brands, setBrands] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  
  // Şifre - Base64 encoded to make it less obvious
  const encodedPassword = 'QXVkaXQyMDI1Kg=='; // 'Audit2025*' in base64

  // Load current settings and brands
  useEffect(() => {
    loadSettings();
    loadBrands();
  }, []);

  const loadBrands = async () => {
    try {
      const response = await api.get('/brands');
      setBrands(response.data || []);
    } catch (error) {
      console.error('Brands loading error:', error);
      setBrands([]);
    }
  };

  const loadSettings = async () => {
    try {
      setInitialLoading(true);
      const response = await api.get('/audit-settings');
      const settingsData = response.data;
      setSettings(settingsData);
      
      // Set form values - show placeholder for existing sensitive data
      form.setFieldsValue({
        facebookToken: settingsData.facebookToken ? '••••••••••••••••' : '', // Show placeholder if exists
        openrouterApiKey: settingsData.openrouterApiKey ? '••••••••••••••••' : '', // Show placeholder if exists
        openrouterModel: settingsData.openrouterModel,
        googleAdsDateRange: settingsData.googleAdsDateRange,
        facebookDateRange: settingsData.facebookDateRange,
        googleAnalyticsDateRange: settingsData.googleAnalyticsDateRange,
        customDataDateRange: settingsData.customDataDateRange,
        facebookDefaultPrompt: settingsData.facebookDefaultPrompt,
        googleAdsDefaultPrompt: settingsData.googleAdsDefaultPrompt,
        googleAnalyticsDefaultPrompt: settingsData.googleAnalyticsDefaultPrompt,
        customDataDefaultPrompt: settingsData.customDataDefaultPrompt
      });
    } catch (error) {
      console.error('Settings yükleme hatası:', error);
      message.error('Ayarlar yüklenirken hata oluştu');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      
      // Only send changed values
      const updateData = {};
      
      // Only update if not placeholder and not empty
      if (values.facebookToken && values.facebookToken !== '••••••••••••••••') {
        updateData.facebookToken = values.facebookToken;
      }
      
      // Only update if not placeholder and not empty
      if (values.openrouterApiKey && values.openrouterApiKey !== '••••••••••••••••') {
        updateData.openrouterApiKey = values.openrouterApiKey;
      }
      
      if (values.openrouterModel !== settings.openrouterModel) {
        updateData.openrouterModel = values.openrouterModel;
      }
      
      if (values.googleAdsDateRange !== settings.googleAdsDateRange) {
        updateData.googleAdsDateRange = values.googleAdsDateRange;
      }
      
      if (values.facebookDateRange !== settings.facebookDateRange) {
        updateData.facebookDateRange = values.facebookDateRange;
      }
      
      if (values.facebookDefaultPrompt !== settings.facebookDefaultPrompt) {
        updateData.facebookDefaultPrompt = values.facebookDefaultPrompt;
      }
      
      if (values.googleAdsDefaultPrompt !== settings.googleAdsDefaultPrompt) {
        updateData.googleAdsDefaultPrompt = values.googleAdsDefaultPrompt;
      }
      
      if (values.googleAnalyticsDateRange !== settings.googleAnalyticsDateRange) {
        updateData.googleAnalyticsDateRange = values.googleAnalyticsDateRange;
      }
      
      if (values.googleAnalyticsDefaultPrompt !== settings.googleAnalyticsDefaultPrompt) {
        updateData.googleAnalyticsDefaultPrompt = values.googleAnalyticsDefaultPrompt;
      }
      
      if (values.customDataDateRange !== settings.customDataDateRange) {
        updateData.customDataDateRange = values.customDataDateRange;
      }
      
      if (values.customDataDefaultPrompt !== settings.customDataDefaultPrompt) {
        updateData.customDataDefaultPrompt = values.customDataDefaultPrompt;
      }

      const response = await api.put('/audit-settings', updateData);
      
      if (response.data.success) {
        message.success('Audit ayarları başarıyla kaydedildi');
        setSettings(response.data.settings);
        
        // Reset sensitive fields to placeholder if they exist
        form.setFieldsValue({
          facebookToken: response.data.settings.facebookToken ? '••••••••••••••••' : '',
          openrouterApiKey: response.data.settings.openrouterApiKey ? '••••••••••••••••' : ''
        });
      }
    } catch (error) {
      console.error('Settings kaydetme hatası:', error);
      
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else {
        message.error('Ayarlar kaydedilirken hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBulkGoogleAudit = () => {
    // Count eligible brands first
    const checkEligibleBrands = async () => {
      let eligibleCount = 0;
      for (const brand of brands) {
        try {
          const response = await auditAPI.getBrandAuditCapabilities(brand.id);
          if (response.data.hasGoogleAds) {
            eligibleCount++;
          }
        } catch (error) {
          // Skip brands with errors
        }
      }
      return eligibleCount;
    };
    
    checkEligibleBrands().then(count => {
      Modal.confirm({
        title: '⚠️ ✨ Toplu Google Ads Audit',
        content: (
          <div>
            <p><strong>{count} adet marka</strong> Google Ads audit'i yapılacak.</p>
            <p>Bu işlem geri alınamaz ve tüm markalar için audit başlayacak.</p>
            <p><strong>Devam etmek istediğinizden emin misiniz?</strong></p>
          </div>
        ),
        icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
        okText: `Evet, ${count} Markayı Audit Et`,
        cancelText: 'İptal Et',
        okType: 'danger',
        onOk: async () => {
          try {
            setBulkLoading(true);
            
            for (const [index, brand] of brands.entries()) {
              try {
                const response = await auditAPI.getBrandAuditCapabilities(brand.id);
                if (response.data.hasGoogleAds) {
                  await auditAPI.triggerBrandAudit(brand.id, {
                    auditType: 'google_ads'
                  });
                  message.success(`${brand.name} - Google Ads audit başlatıldı`);
                  
                  // Son marka değilse 1.1 dakika bekle
                  if (index < brands.length - 1) {
                    message.info(`Sonraki marka için 1.1 dakika bekleniyor... (${index + 1}/${brands.length})`);
                    await new Promise(resolve => setTimeout(resolve, 66000)); // 1.1 dakika = 66000ms
                  }
                }
              } catch (error) {
                console.error(`Brand ${brand.name} audit error:`, error);
                message.error(`${brand.name} - Google Ads audit başlatılamadı`);
              }
            }
            
            message.info('Toplu Google Ads audit işlemi tamamlandı');
            
          } catch (error) {
            console.error('Bulk audit error:', error);
            message.error('Toplu audit işlemi başarısız oldu');
          } finally {
            setBulkLoading(false);
          }
        }
      });
    });
  };

  const handleBulkFacebookAudit = () => {
    // Count eligible brands first
    const checkEligibleBrands = async () => {
      let eligibleCount = 0;
      for (const brand of brands) {
        try {
          const response = await auditAPI.getBrandAuditCapabilities(brand.id);
          if (response.data.hasFacebookAds) {
            eligibleCount++;
          }
        } catch (error) {
          // Skip brands with errors
        }
      }
      return eligibleCount;
    };
    
    checkEligibleBrands().then(count => {
      Modal.confirm({
        title: '⚠️ ✨ Toplu Facebook Ads Audit',
        content: (
          <div>
            <p><strong>{count} adet marka</strong> Facebook Ads audit'i yapılacak.</p>
            <p>Bu işlem geri alınamaz ve tüm markalar için audit başlayacak.</p>
            <p><strong>Devam etmek istediğinizden emin misiniz?</strong></p>
          </div>
        ),
        icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
        okText: `Evet, ${count} Markayı Audit Et`,
        cancelText: 'İptal Et',
        okType: 'danger',
        onOk: async () => {
          try {
            setBulkLoading(true);
            
            for (const [index, brand] of brands.entries()) {
              try {
                const response = await auditAPI.getBrandAuditCapabilities(brand.id);
                if (response.data.hasFacebookAds) {
                  await auditAPI.triggerBrandAudit(brand.id, {
                    auditType: 'facebook'
                  });
                  message.success(`${brand.name} - Facebook Ads audit başlatıldı`);
                  
                  // Son marka değilse 1.1 dakika bekle
                  if (index < brands.length - 1) {
                    message.info(`Sonraki marka için 1.1 dakika bekleniyor... (${index + 1}/${brands.length})`);
                    await new Promise(resolve => setTimeout(resolve, 66000)); // 1.1 dakika = 66000ms
                  }
                }
              } catch (error) {
                console.error(`Brand ${brand.name} audit error:`, error);
                message.error(`${brand.name} - Facebook Ads audit başlatılamadı`);
              }
            }
            
            message.info('Toplu Facebook Ads audit işlemi tamamlandı');
            
          } catch (error) {
            console.error('Bulk audit error:', error);
            message.error('Toplu audit işlemi başarısız oldu');
          } finally {
            setBulkLoading(false);
          }
        }
      });
    });
  };

  const handleBulkGoogleAnalyticsAudit = () => {
    // Count eligible brands first
    const checkEligibleBrands = async () => {
      let eligibleCount = 0;
      for (const brand of brands) {
        try {
          const response = await auditAPI.getBrandAuditCapabilities(brand.id);
          if (response.data.hasGoogleAnalytics) {
            eligibleCount++;
          }
        } catch (error) {
          // Skip brands with errors
        }
      }
      return eligibleCount;
    };
    
    checkEligibleBrands().then(count => {
      Modal.confirm({
        title: '⚠️ ✨ Toplu Google Analytics Audit',
        content: (
          <div>
            <p><strong>{count} adet marka</strong> Google Analytics audit'i yapılacak.</p>
            <p>Bu işlem geri alınamaz ve tüm markalar için audit başlayacak.</p>
            <p><strong>Devam etmek istediğinizden emin misiniz?</strong></p>
          </div>
        ),
        icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
        okText: `Evet, ${count} Markayı Audit Et`,
        cancelText: 'İptal Et',
        okType: 'danger',
        onOk: async () => {
          try {
            setBulkLoading(true);
            
            for (const [index, brand] of brands.entries()) {
              try {
                const response = await auditAPI.getBrandAuditCapabilities(brand.id);
                if (response.data.hasGoogleAnalytics) {
                  await auditAPI.triggerBrandAudit(brand.id, {
                    auditType: 'google_analytics'
                  });
                  message.success(`${brand.name} - Google Analytics audit başlatıldı`);
                  
                  // Son marka değilse 1.1 dakika bekle
                  if (index < brands.length - 1) {
                    message.info(`Sonraki marka için 1.1 dakika bekleniyor... (${index + 1}/${brands.length})`);
                    await new Promise(resolve => setTimeout(resolve, 66000)); // 1.1 dakika = 66000ms
                  }
                }
              } catch (error) {
                console.error(`Brand ${brand.name} audit error:`, error);
                message.error(`${brand.name} - Google Analytics audit başlatılamadı`);
              }
            }
            
            message.info('Toplu Google Analytics audit işlemi tamamlandı');
            
          } catch (error) {
            console.error('Bulk audit error:', error);
            message.error('Toplu audit işlemi başarısız oldu');
          } finally {
            setBulkLoading(false);
          }
        }
      });
    });
  };

  const handleBulkCustomDataAudit = () => {
    // All brands can have ReportsUp audit
    const brandCount = brands.length;
    
    Modal.confirm({
      title: '⚠️ ✨ Toplu ReportsUp Audit',
      content: (
        <div>
          <p><strong>{brandCount} adet marka</strong> ReportsUp audit'i yapılacak.</p>
          <p>Bu işlem geri alınamaz ve tüm markalar için audit başlayacak.</p>
          <p><strong>Devam etmek istediğinizden emin misiniz?</strong></p>
        </div>
      ),
      icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
      okText: `Evet, ${brandCount} Markayı Audit Et`,
      cancelText: 'İptal Et',
      okType: 'danger',
      onOk: async () => {
        try {
          setBulkLoading(true);
          
          for (const [index, brand] of brands.entries()) {
            try {
              await auditAPI.triggerBrandAudit(brand.id, {
                auditType: 'custom_data'
              });
              message.success(`${brand.name} - ReportsUp audit başlatıldı`);
              
              // Son marka değilse 1.1 dakika bekle
              if (index < brands.length - 1) {
                message.info(`Sonraki marka için 1.1 dakika bekleniyor... (${index + 1}/${brands.length})`);
                await new Promise(resolve => setTimeout(resolve, 66000)); // 1.1 dakika = 66000ms
              }
            } catch (error) {
              console.error(`Brand ${brand.name} audit error:`, error);
              message.error(`${brand.name} - ReportsUp audit başlatılamadı`);
            }
          }
          
          message.info('Toplu ReportsUp audit işlemi tamamlandı');
          
        } catch (error) {
          console.error('Bulk audit error:', error);
          message.error('Toplu audit işlemi başarısız oldu');
        } finally {
          setBulkLoading(false);
        }
      }
    });
  };

  const handleBulkAllAudits = () => {
    const brandCount = brands.length;
    const estimatedTime = Math.ceil(brandCount * 4 * 1.5); // 4 audit types, 1.5 min each
    
    Modal.confirm({
      title: '🚀 Tek Tıkla Toplu Audit',
      content: (
        <div>
          <p><strong>Tüm {brandCount} marka</strong> için tüm audit tiplerini başlatacaksınız:</p>
          <ul style={{ margin: '12px 0', paddingLeft: '20px' }}>
            <li>Google Ads audit (uygun markalar için)</li>
            <li>Facebook Ads audit (uygun markalar için)</li>
            <li>Google Analytics audit (uygun markalar için)</li>
            <li>ReportsUp audit (tüm markalar için)</li>
          </ul>
          <p style={{ color: '#666', fontSize: '13px' }}>
            <strong>Tahmini süre:</strong> ~{estimatedTime} dakika
          </p>
          <p style={{ color: '#ff4d4f' }}>
            <strong>⚠️ Bu işlem arka planda çalışır. Ekranı kapatabilirsiniz!</strong>
          </p>
          <p style={{ color: '#666' }}>
            Bugün zaten yapılmış audit'ler otomatik atlanacak. 
            Yarıda kalırsa güvenle yeniden başlatabilirsiniz.
          </p>
          <p><strong>Devam etmek istediğinizden emin misiniz?</strong></p>
        </div>
      ),
      icon: <ExclamationCircleOutlined style={{ color: '#722ED1' }} />,
      okText: `🚀 Evet, Tüm Audit'leri Başlat`,
      cancelText: 'İptal Et',
      okType: 'primary',
      onOk: async () => {
        try {
          setBulkLoading(true);
          
          const response = await auditAPI.bulkAuditAllBrands();
          
          message.success({
            content: (
              <div>
                <p><strong>✅ Toplu audit işlemi başlatıldı!</strong></p>
                <p>📊 Toplam marka: {response.data.totalBrands}</p>
                <p>⏱️ Tahmini süre: {response.data.estimatedTime}</p>
                <p>🖥️ Ekranı kapatabilirsiniz - işlem arka planda devam ediyor</p>
              </div>
            ),
            duration: 10
          });
          
        } catch (error) {
          console.error('Bulk audit error:', error);
          message.error(error.response?.data?.error || 'Toplu audit başlatılamadı');
        } finally {
          setBulkLoading(false);
        }
      }
    });
  };

  const handleCleanupStuckAudits = () => {
    Modal.confirm({
      title: 'Takılı Audit\'leri Temizle',
      content: (
        <div>
          <p>15 dakikadan uzun süren pending audit'ler error olarak işaretlenecek.</p>
          <p>Bu işlem geri alınamaz. Devam etmek istiyor musunuz?</p>
        </div>
      ),
      icon: <ExclamationCircleOutlined />,
      okText: 'Temizle',
      cancelText: 'İptal',
      okType: 'danger',
      onOk: async () => {
        try {
          setBulkLoading(true);
          const response = await auditAPI.cleanupStuckAudits(15);
          message.success(response.data.message);
        } catch (error) {
          console.error('Cleanup error:', error);
          message.error('Temizleme işlemi başarısız oldu');
        } finally {
          setBulkLoading(false);
        }
      }
    });
  };

  const handlePasswordSubmit = () => {
    const correctPassword = atob(encodedPassword);
    if (password === correctPassword) {
      setIsAuthenticated(true);
      message.success('Erişim sağlandı');
    } else {
      message.error('Yanlış şifre!');
      setPassword('');
    }
  };

  const handlePasswordKeyPress = (e) => {
    if (e.key === 'Enter') {
      handlePasswordSubmit();
    }
  };

  // Şifre kontrolü
  if (!isAuthenticated) {
    return (
      <div style={{ padding: 24 }}>
        <Card 
          title="🔒 ✨ Audit Ai Ayarları - Güvenlik Kontrolü" 
          style={{ maxWidth: 500, margin: '50px auto' }}
        >
          <div style={{ textAlign: 'center' }}>
            <p style={{ color: '#666', marginBottom: 24 }}>
              Bu sayfaya erişim için güvenlik şifresi gereklidir.
            </p>
            <Input.Password 
              placeholder="Güvenlik şifresi girin"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={handlePasswordKeyPress}
              size="large"
              style={{ marginBottom: 16 }}
            />
            <Button 
              type="primary" 
              onClick={handlePasswordSubmit}
              size="large"
              block
              loading={loading}
            >
              Erişim Sağla
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (initialLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Card title="✨ Audit Ai Ayarları" style={{  }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            openrouterModel: 'google/gemini-2.0-flash-exp',
            googleAdsDateRange: 'LAST_7_DAYS',
            facebookDateRange: 'last_7d',
            googleAnalyticsDateRange: 'last_7_days',
            customDataDateRange: 'last_week',
            facebookDefaultPrompt: 'Analyze Facebook Ads performance and provide optimization suggestions',
            googleAdsDefaultPrompt: 'Analyze Google Ads performance and provide optimization suggestions',
            googleAnalyticsDefaultPrompt: 'Analyze Google Analytics performance and provide insights',
            customDataDefaultPrompt: 'Bu marka verilerini analiz et ve performans önerilerini ver'
          }}
        >
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item 
                label="Facebook Access Token" 
                name="facebookToken"
                help={settings?.facebookToken ? "Mevcut token korunuyor. Sadece değiştirmek isterseniz yeni token girin." : "Facebook Ads API için access token (opsiyonel)"}
              >
                <Input.Password 
                  placeholder={settings?.facebookToken ? "Mevcut token korunuyor" : "Facebook access token girin"}
                  visibilityToggle
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                label="OpenRouter API Key" 
                name="openrouterApiKey"
                rules={[
                  { 
                    pattern: /^(sk-or-v1-|••••••••••••••••)/, 
                    message: 'OpenRouter API key sk-or-v1- ile başlamalıdır' 
                  }
                ]}
                help={settings?.openrouterApiKey ? "Mevcut API key korunuyor. Sadece değiştirmek isterseniz yeni key girin." : "Ai model erişimi için OpenRouter API anahtarı"}
              >
                <Input.Password 
                  placeholder={settings?.openrouterApiKey ? "Mevcut API key korunuyor" : "sk-or-v1-..."}
                  visibilityToggle
                />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item 
                label="OpenRouter Model" 
                name="openrouterModel"
                rules={[{ required: true, message: 'Model seçimi zorunludur' }]}
              >
                <Input placeholder="google/gemini-2.0-flash-exp" />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={24}>
            <Col span={6}>
              <Form.Item 
                label="Google Ads Varsayılan Tarih Aralığı" 
                name="googleAdsDateRange"
                rules={[{ required: true, message: 'Tarih aralığı seçimi zorunludur' }]}
              >
                <Select placeholder="Tarih aralığı seçin">
                  {GOOGLE_ADS_DATE_RANGES.map(range => (
                    <Option key={range.value} value={range.value}>
                      {range.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item 
                label="Facebook Ads Varsayılan Tarih Aralığı" 
                name="facebookDateRange"
                rules={[{ required: true, message: 'Tarih aralığı seçimi zorunludur' }]}
              >
                <Select placeholder="Tarih aralığı seçin">
                  {FACEBOOK_DATE_RANGES.map(range => (
                    <Option key={range.value} value={range.value}>
                      {range.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item 
                label="Google Analytics Varsayılan Tarih Aralığı" 
                name="googleAnalyticsDateRange"
                rules={[{ required: true, message: 'Tarih aralığı seçimi zorunludur' }]}
              >
                <Select placeholder="Tarih aralığı seçin">
                  {GOOGLE_ANALYTICS_DATE_RANGES.map(range => (
                    <Option key={range.value} value={range.value}>
                      {range.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item 
                label="ReportsUp Varsayılan Tarih Aralığı" 
                name="customDataDateRange"
                rules={[{ required: true, message: 'Tarih aralığı seçimi zorunludur' }]}
              >
                <Select placeholder="Tarih aralığı seçin">
                  {CUSTOM_DATA_DATE_RANGES.map(range => (
                    <Option key={range.value} value={range.value}>
                      {range.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={24}>
            <Col span={6}>
              <Form.Item 
                label="Facebook Ads Varsayılan Prompt" 
                name="facebookDefaultPrompt"
                rules={[{ required: true, message: 'Facebook prompt zorunludur' }]}
              >
                <TextArea 
                  rows={4} 
                  placeholder="Facebook Ads analizi için varsayılan prompt..."
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item 
                label="Google Ads Varsayılan Prompt" 
                name="googleAdsDefaultPrompt"
                rules={[{ required: true, message: 'Google Ads prompt zorunludur' }]}
              >
                <TextArea 
                  rows={4} 
                  placeholder="Google Ads analizi için varsayılan prompt..."
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item 
                label="Google Analytics Varsayılan Prompt" 
                name="googleAnalyticsDefaultPrompt"
                rules={[{ required: true, message: 'Google Analytics prompt zorunludur' }]}
              >
                <TextArea 
                  rows={4} 
                  placeholder="Google Analytics analizi için varsayılan prompt..."
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item 
                label="ReportsUp Varsayılan Prompt" 
                name="customDataDefaultPrompt"
                rules={[{ required: true, message: 'ReportsUp prompt zorunludur' }]}
              >
                <TextArea 
                  rows={4} 
                  placeholder="ReportsUp analizi için varsayılan prompt..."
                />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              icon={<SaveOutlined />}
              size="large"
            >
              Ayarları Kaydet
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* Bulk Operations Section */}
      <Card 
        title={
          <div>
            <Title level={4} style={{ margin: 0, color: '#ff4d4f' }}>
              ⚠️ Toplu Audit İşlemleri
            </Title>
          </div>
        }
        style={{ marginTop: 24, border: '1px solid #ff7875' }}
      >
        <div style={{ marginBottom: 16 }}>
          <p style={{ color: '#666', fontSize: 14, marginBottom: 8 }}>
            <strong>Dikkat:</strong> Bu işlemler tüm uyumlu markalar için audit başlatır ve geri alınamaz.
          </p>
          <p style={{ color: '#666', fontSize: 13 }}>
            Toplu işlemler uzun sürebilir ve sistem kaynaklarını yoğun kullanabilir. 
            Sadece gerekli durumlarda ve sistem yoğunluğunun az olduğu zamanlarda kullanın.
          </p>
        </div>
        
        <Divider />
        
        <Row gutter={[24, 16]}>
          <Col xs={24} lg={6}>
            <Card size="small" style={{ height: '100%' }}>
              <div style={{ textAlign: 'center' }}>
                <GoogleOutlined style={{ fontSize: 32, color: '#4285F4', marginBottom: 12 }} />
                <Title level={5}>✨ Google Ads Toplu Audit</Title>
                <p style={{ color: '#666', fontSize: 13, marginBottom: 16 }}>
                  Google Ads kodu olan tüm markalar için audit başlatır
                </p>
                <Button
                  type="primary"
                  block
                  size="large"
                  icon={<GoogleOutlined />}
                  onClick={handleBulkGoogleAudit}
                  loading={bulkLoading}
                  style={{ 
                    backgroundColor: '#4285F4', 
                    borderColor: '#4285F4',
                    height: 48
                  }}
                >
                  Tüm Google Ads Audit Et
                </Button>
              </div>
            </Card>
          </Col>
          
          <Col xs={24} lg={6}>
            <Card size="small" style={{ height: '100%' }}>
              <div style={{ textAlign: 'center' }}>
                <FacebookOutlined style={{ fontSize: 32, color: '#1877F2', marginBottom: 12 }} />
                <Title level={5}>✨ Facebook Ads Toplu Audit</Title>
                <p style={{ color: '#666', fontSize: 13, marginBottom: 16 }}>
                  Facebook Ads kodu olan tüm markalar için audit başlatır
                </p>
                <Button
                  type="primary"
                  block
                  size="large"
                  icon={<FacebookOutlined />}
                  onClick={handleBulkFacebookAudit}
                  loading={bulkLoading}
                  style={{ 
                    backgroundColor: '#1877F2', 
                    borderColor: '#1877F2',
                    height: 48
                  }}
                >
                  Tüm Facebook Ads Audit Et
                </Button>
              </div>
            </Card>
          </Col>

          <Col xs={24} lg={6}>
            <Card size="small" style={{ height: '100%' }}>
              <div style={{ textAlign: 'center' }}>
                <BarChartOutlined style={{ fontSize: 32, color: '#FF9800', marginBottom: 12 }} />
                <Title level={5}>✨ Google Analytics Toplu Audit</Title>
                <p style={{ color: '#666', fontSize: 13, marginBottom: 16 }}>
                  Google Analytics kodu olan tüm markalar için audit başlatır
                </p>
                <Button
                  type="primary"
                  block
                  size="large"
                  icon={<BarChartOutlined />}
                  onClick={handleBulkGoogleAnalyticsAudit}
                  loading={bulkLoading}
                  style={{ 
                    backgroundColor: '#FF9800', 
                    borderColor: '#FF9800',
                    height: 48
                  }}
                >
                  Tüm Google Analytics Audit Et
                </Button>
              </div>
            </Card>
          </Col>

          <Col xs={24} lg={6}>
            <Card size="small" style={{ height: '100%' }}>
              <div style={{ textAlign: 'center' }}>
                <DatabaseOutlined style={{ fontSize: 32, color: '#52c41a', marginBottom: 12 }} />
                <Title level={5}>✨ ReportsUp Toplu Audit</Title>
                <p style={{ color: '#666', fontSize: 13, marginBottom: 16 }}>
                  Tüm markalar için ReportsUp audit başlatır
                </p>
                <Button
                  type="primary"
                  block
                  size="large"
                  icon={<DatabaseOutlined />}
                  onClick={handleBulkCustomDataAudit}
                  loading={bulkLoading}
                  style={{ 
                    backgroundColor: '#52c41a', 
                    borderColor: '#52c41a',
                    height: 48
                  }}
                >
                  Tüm ReportsUp Audit Et
                </Button>
              </div>
            </Card>
          </Col>
        </Row>
        
        <Row gutter={[24, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={6}>
            <Card size="small" style={{ height: '100%' }}>
              <div style={{ textAlign: 'center' }}>
                <ExclamationCircleOutlined style={{ fontSize: 32, color: '#722ED1', marginBottom: 12 }} />
                <Title level={5}>🚀 Tek Tıkla Toplu Audit</Title>
                <p style={{ color: '#666', fontSize: 13, marginBottom: 16 }}>
                  Tüm markalar için tüm audit tiplerini otomatik başlatır
                </p>
                <Button
                  type="primary"
                  block
                  size="large"
                  icon={<ExclamationCircleOutlined />}
                  onClick={handleBulkAllAudits}
                  loading={bulkLoading}
                  style={{ 
                    backgroundColor: '#722ED1', 
                    borderColor: '#722ED1',
                    height: 48
                  }}
                >
                  🚀 Tüm Audit'leri Başlat
                </Button>
              </div>
            </Card>
          </Col>
          
          <Col xs={24} lg={6}>
            <Card size="small" style={{ height: '100%' }}>
              <div style={{ textAlign: 'center' }}>
                <ClearOutlined style={{ fontSize: 32, color: '#ff4d4f', marginBottom: 12 }} />
                <Title level={5}>Sistem Temizleme</Title>
                <p style={{ color: '#666', fontSize: 13, marginBottom: 16 }}>
                  Takılı audit işlemlerini temizler ve sistemi sıfırlar
                </p>
                <Button
                  danger
                  block
                  size="large"
                  icon={<ClearOutlined />}
                  onClick={handleCleanupStuckAudits}
                  loading={bulkLoading}
                  style={{ 
                    height: 48
                  }}
                >
                  Takılı Auditleri Temizle
                </Button>
              </div>
            </Card>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default AuditSettings;